// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { tutoresApi } from '../services/api';
import { auth } from '../config/firebase';
import { UsersProvider } from '../metodos/userContext';
import { connectSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [nombreEmpresa, setNombreEmpresa] = useState(() => localStorage.getItem('nombreEmpresa') || 'DUFY ASESORIAS');

  // Función para iniciar sesión
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      //console.log("obtenemos credenciales");
      // Una vez autenticado con Firebase, obtener los datos del usuario de tu API
      try {
        const userData = await tutoresApi.getById(userCredential.user.uid);
        
        // Guardar en localStorage para persistencia adicional si lo deseas
        localStorage.setItem('currentUserData', JSON.stringify(userData));
        
        await connectSocket();
        
        setCurrentUserData(userData);
        setError(null);
      } catch (apiError) {
        console.error("Error al obtener datos del usuario desde API:", apiError);
        setError('Error al obtener datos del usuario: ' + apiError.message);
      }
      
      return userCredential;
    } catch (error) {
      setError('Error al iniciar sesión: ' + error.message);
      throw error;
    }
  }

  // Función para cerrar sesión
  async function logout() {
    try {
      await signOut(auth);
      localStorage.removeItem('currentUserData');
      setCurrentUserData(null);
      // ✅ NUEVO: Limpia la empresa al cerrar sesión
      localStorage.removeItem('nombreEmpresa');
      disconnectSocket('');
      setError(null);
    } catch (error) {
      setError('Error al cerrar sesión: ' + error.message);
      throw error;
    }
  }

  function guardarEmpresa(empresa) {
    localStorage.setItem('nombreEmpresa', empresa);
    setNombreEmpresa(empresa);
  }

  // Función para crear un nuevo usuario
  async function signup(email, password, userData) {
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Crear el usuario en tu API con los datos adicionales
      if (userData) {
        const newUserData = {
          ...userData,
          uid: userCredential.user.uid,
          email: userCredential.user.email
        };
        
        await tutoresApi.create(newUserData);
        
        // Actualizar el estado con los datos del usuario
        setCurrentUserData(newUserData);
        localStorage.setItem('currentUserData', JSON.stringify(newUserData));
      }
      
      return userCredential;
    } catch (error) {
      setError('Error al registrar usuario: ' + error.message);
      throw error;
    }
  }

  // Función para resetear contraseña
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Función para actualizar datos del usuario
  async function updateUserData(userData) {
    try {
      if (!currentUser) throw new Error('No hay usuario autenticado');
      
      const updatedUserData = await tutoresApi.update(currentUser.uid, userData);
      
      // Actualizar los datos en el estado y localStorage
      setCurrentUserData(updatedUserData);
      localStorage.setItem('currentUserData', JSON.stringify(updatedUserData));
      
      return updatedUserData;
    } catch (error) {
      setError('Error al actualizar datos del usuario: ' + error.message);
      throw error;
    }
  }

  useEffect(() => {
    // Suscripción a cambios en el estado de autenticación de Firebase
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        //console.log("buscando autenticado");
        // Si hay un usuario autenticado, intentar obtener sus datos desde el localStorage primero
        const storedUserData = localStorage.getItem('currentUserData');
        if (storedUserData) {
          setCurrentUserData(JSON.parse(storedUserData));
        }
        
        // Luego actualizar desde la API para tener los datos más recientes
        try {
          const freshUserData = await tutoresApi.getById(user.uid);
          setCurrentUserData(freshUserData);
          localStorage.setItem('currentUserData', JSON.stringify(freshUserData));
          await connectSocket();
        } catch (error) {
          console.error("Error al obtener datos actualizados del usuario:", error);
          // No establecemos error aquí para no interrumpir la experiencia si los datos locales son suficientes
        }
      } else {
        console.log("error, no hay autenticado");
        // Si no hay usuario autenticado, limpiar los datos
        setCurrentUserData(null);
        localStorage.removeItem('currentUserData');
      }
      
      //console.log("cagado esta vaina");
      setLoading(false);
      //console.log("el loading es ",loading);
    });

    // La función de limpieza que se ejecuta cuando el componente se desmonta
    return unsubscribe;
  }, []);

  const value = {
    currentUser,         // Usuario de Firebase Auth
    currentUserData,     // Datos adicionales del usuario desde tu API
    login,
    logout,
    signup,
    resetPassword,
    updateUserData,      // Nueva función para actualizar datos
    guardarEmpresa,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
